import type { FuelSurchargeWeek } from './types';

export type SurchargeMatch = {
  week: FuelSurchargeWeek;
  /** 'exact' = the date lands inside this week; 'clamped' = nearest available week. */
  basis: 'exact' | 'clamped';
};

/**
 * The surcharge week that governs a delivery date, using the same weekly windows
 * the website widget publishes.
 *
 * EIA only publishes past/current weeks, but loads are routinely delivered in the
 * future — so a delivery date past the last known week clamps to the latest
 * (current) week, and one before the first clamps to the earliest. ISO date
 * strings compare chronologically, so no Date parsing is needed.
 */
export function pickSurchargeForDate(
  weeks: FuelSurchargeWeek[],
  dateISO: string
): SurchargeMatch | null {
  if (!weeks.length || !dateISO) return null;

  const sorted = [...weeks].sort((a, b) => a.weekStartISO.localeCompare(b.weekStartISO));

  const exact = sorted.find((w) => dateISO >= w.weekStartISO && dateISO <= w.weekEndISO);
  if (exact) return { week: exact, basis: 'exact' };

  const earliest = sorted[0];
  const latest = sorted[sorted.length - 1];

  if (dateISO > latest.weekEndISO) return { week: latest, basis: 'clamped' };
  if (dateISO < earliest.weekStartISO) return { week: earliest, basis: 'clamped' };

  // A gap between contiguous weeks shouldn't happen; if it does, don't guess.
  return null;
}

const formatPct = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(1)}%`;

/** e.g. "3% delivery · 1.5% material" — the surcharge for the picked week. */
export function describeSurcharge(match: SurchargeMatch): string {
  const { deliveryPct, materialPct } = match.week;
  return `${formatPct(deliveryPct)} delivery · ${formatPct(materialPct)} material`;
}
